import { Component, computed, effect, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PopoverModule, Popover } from 'primeng/popover';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { AuthService } from '../../../../../core/services/auth/auth.service';
import { UserNotificationsService } from '../../../../../core/services/user-notifications/user-notifications.service';
import {
  UserNotification,
  UserNotificationType,
} from '../../../../../core/services/user-notifications/types/user-notification.type';

interface NotificationDestination {
  path: (string | number)[];
  queryParams: Record<string, string | number>;
}

@Component({
  selector: 'app-dropdown-notifications',
  imports: [CommonModule, PopoverModule, DividerModule, TooltipModule],
  templateUrl: './dropdown-notifications.html',
})
export class DropdownNotifications implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly notificationsService = inject(UserNotificationsService);
  private readonly router = inject(Router);

  @ViewChild('notifMenu') notifMenu?: Popover;

  readonly unreadCount = signal(0);
  readonly notifications = signal<UserNotification[]>([]);
  readonly loading = signal(false);
  readonly markingAllRead = signal(false);
  readonly expandedIds = signal<Set<number>>(new Set());
  readonly tooltip = computed(() => {
    const n = this.unreadCount();
    if (n === 0) return 'No tienes notificaciones sin leer';
    return n === 1 ? '1 notificación sin leer' : `${n} notificaciones sin leer`;
  });

  private pollSub: Subscription | null = null;

  constructor() {
    effect(() => {
      const authenticated = !!this.authService.currentUser();
      this.stopPolling();
      if (authenticated) this.startPolling();
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  onOpen(): void {
    this.loadFirstPage();
  }

  markAsRead(notification: UserNotification): void {
    if (notification.readAt) return;
    this.notificationsService.markAsRead(notification.id).subscribe({
      next: () => {
        this.notifications.update((list) =>
          list.map((n) =>
            n.id === notification.id
              ? { ...n, readAt: new Date().toISOString() }
              : n,
          ),
        );
        this.unreadCount.update((n) => Math.max(0, n - 1));
      },
    });
  }

  hasNotifiedEmails(notification: UserNotification): boolean {
    return (notification.metadata?.notifiedEmails?.length ?? 0) > 0;
  }

  notifiedEmailsLabel(notification: UserNotification): string {
    const count = notification.metadata?.notifiedEmails?.length ?? 0;
    return count === 1 ? 'Se envió a 1 alumno' : `Se envió a ${count} alumnos`;
  }

  isExpanded(notificationId: number): boolean {
    return this.expandedIds().has(notificationId);
  }

  toggleExpand(notificationId: number, event: Event): void {
    event.stopPropagation();
    this.expandedIds.update((set) => {
      const next = new Set(set);
      if (next.has(notificationId)) next.delete(notificationId);
      else next.add(notificationId);
      return next;
    });
  }

  private readonly destinations: Partial<
    Record<UserNotificationType, (notification: UserNotification) => NotificationDestination | null>
  > = {
    [UserNotificationType.LETTER_TO_CREATE]: (notification) => this.letterToCreateDestination(notification),
    [UserNotificationType.BANK_MATCH_AUTOMATIC]: () => ({
      path: ['/crm/treasury/bank-match/conciliated'],
      queryParams: {},
    }),
    [UserNotificationType.ENROLLMENT_WITH_ACTIVE_COURSE]: (notification) =>
      notification.metadata?.subjectGroupId
        ? { path: ['/crm/groups/subject', notification.metadata.subjectGroupId], queryParams: {} }
        : null,
  };

  onNotificationClick(notification: UserNotification): void {
    this.markAsRead(notification);
    const toDestination =
      this.destinations[notification.type] ?? ((other: UserNotification) => this.sessionDestination(other));
    const destination = toDestination(notification);
    if (!destination) return;

    this.notifMenu?.hide();
    this.router.navigate(destination.path, { queryParams: destination.queryParams });
  }

  private sessionDestination(notification: UserNotification): NotificationDestination | null {
    const groupId = notification.metadata?.groupId;
    const sessionId = notification.metadata?.sessionId;
    if (!groupId || !sessionId) return null;

    return {
      path: ['/crm/groups/details', groupId],
      queryParams: { scrollToSession: sessionId },
    };
  }

  private letterToCreateDestination(notification: UserNotification): NotificationDestination | null {
    const metadata = notification.metadata;
    if (
      !metadata?.universityId ||
      !metadata.degreeId ||
      !metadata.gradeOfferedId ||
      !metadata.academicYearId ||
      !metadata.year ||
      !metadata.letter
    ) {
      return null;
    }

    return {
      path: ['/crm/year-course-letter'],
      queryParams: {
        universityId: metadata.universityId,
        degreeId: metadata.degreeId,
        degreePlanId: metadata.gradeOfferedId,
        academicYearId: metadata.academicYearId,
        year: metadata.year,
        letter: metadata.letter,
      },
    };
  }

  markAllAsRead(): void {
    if (this.markingAllRead() || this.unreadCount() === 0) return;
    this.markingAllRead.set(true);
    this.notificationsService.markAllAsRead().subscribe({
      next: () => {
        const now = new Date().toISOString();
        this.notifications.update((list) =>
          list.map((n) => (n.readAt ? n : { ...n, readAt: now })),
        );
        this.unreadCount.set(0);
        this.markingAllRead.set(false);
      },
      error: () => this.markingAllRead.set(false),
    });
  }

  formatRelativeTime(iso: string): string {
    const diffSec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (diffSec < 60) return 'hace un momento';
    const diffMin = Math.round(diffSec / 60);
    if (diffMin < 60) return `hace ${diffMin} min`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `hace ${diffH} h`;
    const diffDays = Math.round(diffH / 24);
    if (diffDays < 30) return `hace ${diffDays} d`;
    return new Date(iso).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
    });
  }

  private startPolling(): void {
    this.refreshCount();
    this.pollSub = interval(60_000).subscribe(() => this.refreshCount());
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  private refreshCount(): void {
    this.notificationsService.unreadCount().subscribe({
      next: (resp) => this.unreadCount.set(resp.data?.count ?? 0),
      error: () => {
        /* silent: error transitorio del polling */
      },
    });
  }

  private loadFirstPage(): void {
    this.loading.set(true);
    this.notificationsService.list(1, 20).subscribe({
      next: (resp) => {
        this.notifications.set(resp.data ?? []);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }
}
